import { ChangeDetectionStrategy, Component, computed, inject, signal, TemplateRef, viewChild, ViewContainerRef } from '@angular/core';
import { email, FieldState, form, FormField, required, validate } from '@angular/forms/signals';
import { provideTranslocoScope, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SdsButton } from '@nieuwestroom/sds-ui/button';
import { SdsDialog, SdsDialogFooter, SdsDialogHeader } from '@nieuwestroom/sds-ui/dialog';
import { SdsError, SdsFormField, SdsLabel } from '@nieuwestroom/sds-ui/form-field';
import { SdsInput } from '@nieuwestroom/sds-ui/input';
import * as Sentry from '@sentry/angular';
import { NgpDialogContext, NgpDialogManager } from 'ng-primitives/dialog';
import { NgpToastManager } from 'ng-primitives/toast';

import { CustomersMembersService, MembersInviteRequest, ProblemDocument } from '@customer-portal/api';
import { NS_TOAST_INTENTS, NsToast } from '@customer-portal/indicators/toast';
import { ActiveMemberState } from '@customer-portal/shared/account/data-access';
import { MetricId } from '@customer-portal/shared/analytics';

interface InviteData {
  firstName: string;
  lastName: string;
  emailAddress: string;
}

@Component({
  selector: 'nscp-invite-manager-step',
  imports: [TranslocoDirective, FormField, SdsFormField, SdsLabel, SdsInput, SdsError, SdsDialog, SdsDialogHeader, SdsDialogFooter, SdsButton, MetricId],
  templateUrl: './invite-manager-step.html',
  styleUrl: './invite-manager-step.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope('memberManagement', 'core')],
  host: { class: 'nscp-invite-manager-step' },
})
export class InviteManagerStep {
  private readonly membersService = inject(CustomersMembersService);
  private readonly activeMemberState = inject(ActiveMemberState);
  private readonly toastManager = inject(NgpToastManager);
  private readonly translocoService = inject(TranslocoService);
  private readonly dialogManager = inject(NgpDialogManager);
  // ng-primitives otherwise falls back to the root component's ViewContainerRef, which is not
  // available in every host (e.g. TestBed). Mirrors CommunicationPreferences.
  private readonly viewContainerRef = inject(ViewContainerRef);

  private readonly customerId = this.activeMemberState.customerId;
  private readonly membersListResource = this.membersService.membersList({ customerId: this.customerId });

  private readonly errorCannotCreateUser = viewChild.required<TemplateRef<NgpDialogContext>>('errorCannotCreateUser');
  private readonly errorCannotCreateExistingOrganizationMember = viewChild.required<TemplateRef<NgpDialogContext>>('errorCannotCreateExistingOrganizationMember');

  private readonly inviteModel = signal<InviteData>({ firstName: '', lastName: '', emailAddress: '' });
  private readonly inviteBody = signal<MembersInviteRequest | undefined>(undefined);
  private readonly savingState = signal(false);

  public readonly inputForm = form(this.inviteModel, (schemaPath) => {
    required(schemaPath.firstName);
    required(schemaPath.lastName);
    required(schemaPath.emailAddress);
    email(schemaPath.emailAddress);
    validate(schemaPath.emailAddress, (field) => {
      const members = this.membersListResource.value()?.content?._embedded?.members ?? [];
      const emailValue = field.value()?.toLowerCase().trim();
      if (!emailValue) return [];
      return members.some((m) => m.emailAddress?.toLowerCase() === emailValue) ? [{ kind: 'member-email-exists' }] : [];
    });
  });

  public readonly isSaving = this.savingState.asReadonly();

  public readonly canSubmit = computed(() => !this.inputForm.firstName().invalid() && !this.inputForm.lastName().invalid() && !this.inputForm.emailAddress().invalid());

  protected readonly emailAlreadyExists = computed(
    () =>
      this.inputForm
        .emailAddress()
        .errors()
        ?.some((error) => error.kind === 'member-email-exists') ?? false,
  );

  protected readonly firstNameErrorMessage = computed(() => this.fieldErrorMessage(this.inputForm.firstName(), 'memberManagement.member.firstNameLabel'));
  protected readonly lastNameErrorMessage = computed(() => this.fieldErrorMessage(this.inputForm.lastName(), 'memberManagement.member.lastNameLabel'));

  protected readonly emailErrorMessage = computed(() => {
    if (this.emailAlreadyExists()) {
      return this.translocoService.translate('memberManagement.invite.msg.cannotCreateUserWithEmail.fieldError');
    }
    return this.fieldErrorMessage(this.inputForm.emailAddress(), 'memberManagement.member.emailAddressLabel');
  });

  public async save(): Promise<boolean> {
    this.savingState.set(true);

    try {
      this.inviteBody.set({
        firstName: this.inputForm.firstName().value() || undefined,
        lastName: this.inputForm.lastName().value() || undefined,
        emailAddress: this.inputForm.emailAddress().value() || undefined,
      });

      const membersInviteRef = await this.membersService.invokeMembersInvite({ customerId: this.customerId, body: this.inviteBody });

      if (membersInviteRef.status() === 'error') {
        const problem = membersInviteRef.error() as ProblemDocument;

        if (problem.status === 409 && problem.detail === 'User already exists as organization member') {
          Sentry.logger.warn('Member invite failed: user already exists', { statusCode: problem.status, detail: problem.detail });
          this.dialogManager.open(this.errorCannotCreateExistingOrganizationMember(), { viewContainerRef: this.viewContainerRef });
        } else {
          Sentry.logger.error('Member invite failed', { statusCode: problem.status, detail: problem.detail });
          this.dialogManager.open(this.errorCannotCreateUser(), { viewContainerRef: this.viewContainerRef });
        }
        return false;
      }

      if (!membersInviteRef.hasValue()) {
        return false;
      }

      this.showInviteSentToast();
      return true;
    } finally {
      this.savingState.set(false);
    }
  }

  private fieldErrorMessage(field: FieldState<string>, fieldNameKey: string): string | undefined {
    if (!field.dirty() || !field.invalid()) {
      return undefined;
    }

    const kind = field.errors()?.[0]?.kind;
    if (kind === 'required') {
      return this.translocoService.translate('core.messages.error.inputRequired', { fieldName: this.translocoService.translate(fieldNameKey).toLowerCase() });
    }
    if (kind === 'email') {
      return this.translocoService.translate('core.messages.error.inputInvalidEmail');
    }
    return undefined;
  }

  private showInviteSentToast(): void {
    const body = this.inviteBody();
    this.toastManager.show(NsToast, {
      context: {
        intent: NS_TOAST_INTENTS.CONFIRM,
        header: this.translocoService.translate('memberManagement.invite.msg.inviteSent.heading'),
        description: this.translocoService.translate('memberManagement.invite.msg.inviteSent.details', {
          firstName: body?.firstName,
          lastName: body?.lastName,
          emailAddress: body?.emailAddress,
        }),
      },
    });
  }
}
